import { clearMerchantIdCache } from '@api';
import { getOrgConfig } from '@api/organization';
import { setDisplayCurrency } from '@utils/currency';
import { Page } from '@components/common/Page';
import { COMPANY_NAME, VENDOR_NAV_ITEMS, type VendorNavItem } from '@constants/sidebar';
import { Box, Menu, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { CaretLeft, CaretRight, SignOut, UserCircle } from '@phosphor-icons/react';
import { useLeftSidebar } from '@hooks/sidebar/useLeftSidebar';
import useApp from '@providers/App/useApp';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FC, type ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

import styles from './VendorLayout.module.css';

/** `navItems` defaults to the manufacturer portal; the customer portal passes B2B_NAV_ITEMS. */
export const VendorSidebar: FC<{ navItems?: VendorNavItem[] }> = ({ navItems = VENDOR_NAV_ITEMS }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { currentUser } = useApp();

    /*
     * THE SITE READS THE BRAND'S NUMBERS THE BRAND'S WAY. Every figure formatter in the app
     * groups digits by the organisation's currency (16,70,000 for an INR brand, 1,670,000 for
     * AED), seeded once per session from the org config. The brand shell seeds it in its own
     * sidebar; a site login never renders that shell, so without this the portal fell back to
     * the default and a UAE manufacturer read Indian grouping on its queue.
     */
    const organizationId = currentUser?.organizationId ?? null;
    const { data: orgConfig } = useQuery({
        queryKey: ['org-config', organizationId],
        queryFn: () => getOrgConfig(organizationId as string),
        enabled: !!organizationId,
        staleTime: 5 * 60 * 1000,
    });
    useEffect(() => {
        setDisplayCurrency(orgConfig?.currency);
    }, [orgConfig?.currency]);

    /*
     * The collapse state is not ours. `Page` already owns it — persisted under
     * `sidebar-collapsed`, and forced closed below 1100px — and `LeftSidebar`
     * already animates the width between 250px and 60px. All this sidebar was
     * missing was reading it, which is why at 60px it squeezed its labels instead
     * of becoming a rail.
     */
    const sidebar = useLeftSidebar();
    const collapsed = sidebar ? !sidebar.isOpen : false;
    const toggle = sidebar?.toggle ?? (() => {});

    // The account is shared on a shop floor, so a name is a courtesy rather than an identity.
    // Read from the app's own user rather than by parsing localStorage, which was one more
    // place for the two to disagree.
    const displayName =
        [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ')
        || currentUser?.email
        || 'Site';

    const renderItem = (item: VendorNavItem) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;

        const button = (
            <button
                key={item.path}
                type="button"
                disabled={!item.available}
                onClick={() => item.available && navigate(item.path)}
                className={[
                    styles.navItem,
                    collapsed ? styles.navItemCollapsed : '',
                    isActive ? styles.navItemActive : '',
                    item.available ? '' : styles.navItemUnavailable,
                ]
                    .filter(Boolean)
                    .join(' ')}
            >
                {Icon && <Icon size={16} weight={isActive ? 'fill' : 'regular'} />}
                {!collapsed && <span>{item.label}</span>}
            </button>
        );

        /*
         * Collapsed, the label is the only thing identifying the icon, so every item
         * needs a tooltip — not just the unavailable ones. An unavailable item still
         * has to say why it cannot be clicked, so the two reasons are combined rather
         * than one replacing the other.
         */
        const tooltip = item.available
            ? collapsed
                ? item.label
                : null
            : collapsed
              ? `${item.label} — coming soon`
              : 'Coming soon';

        if (!tooltip) return button;

        return (
            <Tooltip key={item.path} label={tooltip} position="right" withArrow>
                {/* Tooltip needs an element that still receives events; a disabled button does not. */}
                <span className={styles.navItemWrap}>{button}</span>
            </Tooltip>
        );
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        clearMerchantIdCache();
        queryClient.clear();
        navigate('/login');
    };

    return (
        <Stack p="sm" gap="sm" className={styles.sidebar}>
            <Box>
                <div
                    className={
                        collapsed
                            ? `${styles.brand} ${styles.brandCollapsed}`
                            : styles.brand
                    }
                >
                    <div className={styles.brandMark}>R</div>
                    {!collapsed && (
                        <span className={styles.brandName}>{COMPANY_NAME}</span>
                    )}
                    <Tooltip
                        label={collapsed ? 'Expand menu' : 'Collapse menu'}
                        position="right"
                        withArrow
                    >
                        <button
                            type="button"
                            onClick={toggle}
                            aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
                            aria-expanded={!collapsed}
                            className={styles.toggleButton}
                        >
                            {collapsed ? (
                                <CaretRight size={16} weight="bold" />
                            ) : (
                                <CaretLeft size={16} weight="bold" />
                            )}
                        </button>
                    </Tooltip>
                </div>
                {/* The section label is the one thing with no icon to fall back to. */}
                {!collapsed && (
                    <Text mt={12} className={styles.sectionLabel}>
                        Menu
                    </Text>
                )}
            </Box>

            <Box className={styles.navList}>
                <Stack gap={4}>{navItems.map(renderItem)}</Stack>
            </Box>

            <Box>
                <Menu shadow="md" width={200} position="right-end">
                    <Menu.Target>
                        <UnstyledButton
                            className={
                                collapsed
                                    ? `${styles.userButton} ${styles.userButtonCollapsed}`
                                    : styles.userButton
                            }
                            aria-label={displayName}
                        >
                            <UserCircle size={20} weight="duotone" />
                            {!collapsed && (
                                <span className={styles.userName}>{displayName}</span>
                            )}
                        </UnstyledButton>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Menu.Item
                            leftSection={<SignOut size={16} />}
                            color="red"
                            onClick={handleLogout}
                        >
                            Logout
                        </Menu.Item>
                    </Menu.Dropdown>
                </Menu>
            </Box>
        </Stack>
    );
};

interface VendorLayoutProps {
    children: ReactNode;
    navItems?: VendorNavItem[];
}

const VendorLayout: FC<VendorLayoutProps> = ({ children, navItems }) => (
    <Page
        backgroundColor="white"
        withPadding={false}
        leftSidebar={<VendorSidebar navItems={navItems} />}
        // Six short labels; the operator rail's 250px reads as empty grey here.
        leftSidebarWidth={210}
    >
        {/* Fills, rather than centring. The old wrapper was a Center at screen height, which
            pushed a table's first row into the middle of the viewport and let the rest of it
            run off the bottom. */}
        <Box className="h-full w-full">{children}</Box>
    </Page>
);

export default VendorLayout;
